import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Camera, MapPin } from "lucide-react";
import { format } from "date-fns";
import { formatCardDate } from "@/design/format";
import { WorkspaceHeader } from "@/shell/WorkspaceHeader";
import { MilestoneStrip, Pill, Avatar, Card, CardBody } from "@/design/components";
import { useSeed, useProject, useMilestones, useOrgIndex, useActiveProfile, useMoney } from "@/domain/hooks";

const SITE_IMAGES = [
  "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?auto=format&fit=crop&w=600&q=60",
  "https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=600&q=60",
  "https://images.unsplash.com/photo-1486718448742-163732cd1544?auto=format&fit=crop&w=600&q=60",
];

export function ProjectSummaryPage() {
  const { workspaceId, projectId = "" } = useParams();
  const seed = useSeed();
  const money = useMoney();
  const project = useProject(projectId);
  const milestones = useMilestones(projectId);
  const orgs = useOrgIndex();
  const profile = useActiveProfile();
  const navigate = useNavigate();

  if (!project) return null;
  const lead = orgs[project.leadContractorId];
  const visibleMilestones = profile.group === "funder"
    ? milestones.filter((m) => ["disbursed", "ready_for_disbursement", "evidence_submitted"].includes(m.status))
    : milestones;
  const canManage = profile.group !== "funder";
  const evidence = seed.spendItems.filter((i) => {
    const req = seed.spendRequests.find((r) => r.id === i.spendRequestId);
    return req?.projectId === projectId && i.evidence.length > 0;
  }).slice(0, 3);
  const latestDisbursement = seed.disbursements.find((d) => d.state === "completed");

  return (
    <div className="flex min-h-screen flex-col bg-bg">
      <WorkspaceHeader />
      <div className="flex items-center justify-between border-b border-line bg-card px-6 py-2">
        {canManage ? (
          <div className="inline-flex items-center rounded-md border border-line bg-card p-0.5">
            <button onClick={() => navigate(`/workspace/${workspaceId}/projects/${projectId}`)} className="rounded px-3 py-1 text-sm text-mute hover:text-ink">
              Dashboard
            </button>
            <span className="rounded px-3 py-1 text-sm font-medium bg-subtle text-ink">Presentation view</span>
          </div>
        ) : (
          <button onClick={() => navigate(`/workspace/${workspaceId}`)} className="flex items-center gap-1.5 text-sm text-mute hover:text-ink">
            <ArrowLeft className="h-4 w-4" /> Back to portfolio
          </button>
        )}
      </div>

      <main className="mx-auto w-full max-w-4xl px-6 py-8">
        {/* A. Hero. Scrim uses the same rgb triplet as the --ink-900 design
            token (15,17,41) rather than an ad-hoc approximation, with the
            top-edge floor raised to 0.6 (was 0.2) so the eyebrow label stays
            legible over a light/busy cover image. Text uses the ink-scale
            tokens ("sidebar, hero, report dark tiles" per tailwind.config.ts)
            instead of raw white.
            Both branches set backgroundImage inline rather than leaning on the
            bg-gradient-to-br from-ink/80 to-ink utility classes: `ink` is a
            bare `var(--text-primary)` token (tailwind.config.ts), and
            Tailwind 3.4 can't compute an opacity modifier against a color
            it can't statically parse, so `from-ink/80` silently generates no
            CSS at all — no cover photo meant no background whatsoever behind
            near-white hero text. */}
        <div className="relative overflow-hidden rounded-xl">
          <div
            className="aspect-[16/9] w-full"
            style={
              project.coverImageUrl
                ? { backgroundImage: `linear-gradient(to top, rgba(15,17,41,0.88), rgba(15,17,41,0.6)), url(${project.coverImageUrl})`, backgroundSize: "cover", backgroundPosition: "center" }
                : { backgroundImage: "linear-gradient(to bottom right, rgba(15,17,41,0.8), rgb(15,17,41))" }
            }
          />
          <div className="absolute inset-x-0 bottom-0 p-6 text-inkText1">
            <p className="text-xs font-medium uppercase tracking-widest text-inkText2">{seed.programme.name}</p>
            <h1 className="mt-1 text-4xl font-semibold tracking-tight">{project.name}</h1>
            <span className="mt-2 inline-flex"><Pill variant="success" dot>Active</Pill></span>
          </div>
        </div>
        <p className="mt-5 text-base leading-relaxed text-mute">{project.description}</p>

        {/* About this site — helps recognise the station among many */}
        <section className="mt-8">
          <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-faint">About this site</h3>
          <div className="grid grid-cols-3 gap-3">
            {SITE_IMAGES.map((src, i) => (
              <div key={i} className="aspect-[4/3] overflow-hidden rounded-lg border border-line bg-subtle">
                <div className="h-full w-full" style={{ backgroundImage: `url(${src})`, backgroundSize: "cover", backgroundPosition: "center" }} />
              </div>
            ))}
          </div>
          <div className="mt-3 rounded-lg border border-l-2 border-line border-l-brand bg-card p-4">
            <p className="text-sm font-medium text-ink">What makes this site distinctive</p>
            <p className="mt-1 text-sm text-mute">
              {project.name.includes("Entebbe")
                ? "On the airport approach road, flat reclaimed ground bordered by wetland to the south. Access is good but the high water table drove the deeper foundation works. This is the programme's flagship site and the first scheduled for handover."
                : `${project.district} district. ${project.currentPhase} is the current phase, led by ${lead?.name}. Site conditions and access are typical for the region.`}
            </p>
          </div>
        </section>

        {/* B. Lead contractor + timeline */}
        <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-faint">Lead contractor</h3>
            <div className="mt-2 flex items-center gap-3">
              <Avatar initials={lead?.name.slice(0, 2).toUpperCase() ?? "?"} size={40} tone="brand" />
              <div>
                <p className="font-medium text-ink">{lead?.name}</p>
                <p className="text-sm text-faint">Prime contractor</p>
              </div>
            </div>
          </div>
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-faint">Timeline</h3>
            <dl className="mt-2 space-y-1 text-sm">
              <Row label="Start" value={format(new Date(seed.programme.startDate), "MMM yyyy")} />
              <Row label="Current milestone" value={milestones.find((m) => m.current)?.name ?? "—"} />
              <Row label="Next milestone" value="Roofing" />
            </dl>
          </div>
        </div>

        {/* C. Milestone strip */}
        <section className="mt-8">
          <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-faint">Milestones</h3>
          <MilestoneStrip milestones={visibleMilestones} variant="full" />
        </section>

        {/* D. What's happening now */}
        <section className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div>
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-faint">What's happening now</h3>
            <div className="grid grid-cols-3 gap-2">
              {evidence.map((i) => (
                <div key={i.id} className="rounded-md border border-line p-2">
                  <div className="flex h-16 items-center justify-center rounded bg-subtle text-faint"><Camera className="h-4 w-4" /></div>
                  <p className="mt-1.5 truncate text-[11px] font-medium text-ink">{i.description}</p>
                  <p className="truncate text-[10px] text-faint">{i.supplier}</p>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-faint">Latest disbursement</h3>
            {latestDisbursement && (
              <Card>
                <CardBody className="space-y-1">
                  <p className="text-lg font-semibold tabular-nums text-ink">{money(latestDisbursement.amount)}</p>
                  <p className="text-sm text-mute">{seed.milestones.find((m) => m.id === latestDisbursement.milestoneId)?.name}</p>
                  <p className="text-xs text-faint">Approved {latestDisbursement.approvedAt ? formatCardDate(latestDisbursement.approvedAt) : ""} · ref {latestDisbursement.bankingTransactionRef}</p>
                </CardBody>
              </Card>
            )}
          </div>
        </section>

        {/* E. Budget at a glance */}
        <section className="mt-8">
          <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-faint">Budget at a glance</h3>
          <p className="text-3xl font-semibold tracking-tight tabular-nums text-ink">83% of milestones disbursed</p>
          <p className="mt-1 text-sm text-mute">12% under-spent vs plan to date.</p>
          <div className="mt-4 flex h-6 overflow-hidden rounded-md border border-line">
            <Segment label="Disbursed" value={project.evidenced} total={project.budgeted} color="var(--status-success)" />
            <Segment label="Committed" value={project.spent - project.evidenced} total={project.budgeted} color="var(--accent-base)" />
            <Segment label="Remaining" value={project.budgeted - project.spent} total={project.budgeted} color="var(--border-strong)" />
          </div>
          <div className="mt-2 flex flex-wrap gap-4 text-xs text-faint">
            <Legend color="var(--status-success)" label="Disbursed" />
            <Legend color="var(--accent-base)" label="Committed" />
            <Legend color="var(--border-strong)" label="Remaining" />
          </div>
        </section>

        {/* F. Beneficiaries and impact */}
        <section className="mt-8">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-faint">Beneficiaries and impact</h3>
          <p className="text-base leading-relaxed text-mute">
            The Entebbe station will serve the airport corridor and surrounding sub-counties, an area home to roughly 320,000 residents and one of the busiest transport hubs in the country. Response times in the corridor are currently constrained by the distance to the nearest station in central Kampala. Once commissioned, the station is expected to reduce average response time in the corridor from 28 minutes to under 12, with a four-bay appliance hall and a resident crew of 24. The station also provides a regional training yard, intended to lift firefighting capability across the surrounding districts over the life of the programme.
          </p>
        </section>

        {/* G. Documents */}
        {/* Not in MVP scope, come back later — document download buttons (procurement plan extract, latest progress report, latest signed declaration) commented out until wired to real files. */}
        {/* <section className="mt-8">
          <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-faint">Documents</h3>
          <div className="flex flex-wrap gap-2">
            {["Procurement plan extract", "Latest progress report", "Latest signed declaration"].map((d) => (
              <Button key={d} variant="secondary" size="sm"><Download className="h-4 w-4" /> {d}</Button>
            ))}
          </div>
        </section> */}
      </main>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <dt className="text-faint">{label}</dt>
      <dd className="font-medium text-ink">{value}</dd>
    </div>
  );
}
function Segment({ value, total, color }: { label: string; value: number; total: number; color: string }) {
  return <div style={{ width: `${Math.max(0, (value / total) * 100)}%`, background: color }} className="h-full" />;
}
function Legend({ color, label }: { color: string; label: string }) {
  return <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: color }} /> {label}</span>;
}
